import React, { useEffect, useRef, useState } from 'react';
import { App, Button, Card, Collapse, Divider, Dropdown, Modal, Space, Tag, Typography, Alert, Statistic, Skeleton, Popover, Tooltip } from 'antd';
import { LinkOutlined, ReloadOutlined, UserSwitchOutlined, PlusOutlined, ThunderboltOutlined, TrophyOutlined, EyeOutlined, ArrowUpOutlined, ArrowDownOutlined, ExportOutlined, CopyOutlined, LogoutOutlined, UserOutlined, DownOutlined, UpOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { useLeadOpsActivityLog, useLeadOpsCheckSw, useLeadOpsCheckMonday, useLeadOpsCompetitors, useLeadOpsConfig, useLeadOpsCreate, useLeadOpsHistory, useLeadOpsMarkDuplicate, useLeadOpsReassign, useLeadOpsTrafficSources } from '../hooks/useLeadOps';
import { useAuth } from '../hooks/useAuth';
import { LeadOpsMatch, leadOpsApi } from '../api/leadOps';
import { formatCompact, formatDuration, formatHumanDateTime, formatNumber, formatPercent, daysSince } from '../utils/format';
import { SearchBar } from './LeadOps/components/SearchBar';
import { useOnboarding, OnboardingTour } from './LeadOps/components/OnboardingTour';
import { MatchPreview, getInventoryStatus, inventoryStatusColor } from './LeadOps/components/CrmPanel';
import { PublisherSummary } from './LeadOps/components/PublisherSummary';
import { CompetitorsCard, TrafficSourcesCard } from './LeadOps/components/SimilarwebPanel';
import { InventoryPitchModal } from './LeadOps/components/InventoryPitchModal';
import { UnsoldInventoryPanel } from './LeadOps/components/UnsoldInventoryPanel';
import { useLeadWorkbench } from './LeadOps/hooks/useLeadWorkbench';
import { useLeadOpsMode } from './LeadOps/hooks/useLeadOpsMode';
import { copyTextToClipboard } from './LeadOps/utils/clipboard';
import { isArchivedInventory, sortInventoryForAdvertiser } from './LeadOps/utils/adFormats';
import { buildTopGeosLine } from './LeadOps/utils/pitch';
import './LeadOps/leadOps.css';
import {
  domainsAreRelated,
  isExactDomainMatch as matchIsExactDomainMatch,
  isLeadsBoard as matchIsLeadsBoard,
  isMatchQualified,
  isRelatedDomain as matchIsRelatedDomain,
  primaryRank as rankLeadMatch,
  resolveActivityReassign,
  resolveInventoryPitchDomains,
} from './LeadOps/utils/matching';

const { Title, Text } = Typography;

const LeadOpsPage: React.FC = () => {
  const { message } = App.useApp();
  const {
    domainInput,
    setDomainInput,
    lastReadDomain,
    lastReadDomainRef,
    setLastReadDomainTracked,
    hasFullData,
    setHasFullData,
    hasCrmData,
    setHasCrmData,
    crmSnapshot,
    crmSnapshotRef,
    setCrmSnapshotTracked,
    swSnapshot,
    swSnapshotRef,
    setSwSnapshotTracked,
    suggestedActions,
    suggestedActionsRef,
    setSuggestedActionsTracked,
    lastDomainCanonical,
    setLastDomainCanonical,
    lastAction,
    setLastAction,
    saveWorkbenchState,
    clearWorkbenchState,
  } = useLeadWorkbench();
  const [collapseKeys, setCollapseKeys] = useState<string[]>([]);
  const [pendingReassignId, setPendingReassignId] = useState<string | null>(null);
  const [pendingDuplicateId, setPendingDuplicateId] = useState<string | null>(null);
  const [pitchInventoryMatch, setPitchInventoryMatch] = useState<any>(null);
  const [pitchInventoryDomain, setPitchInventoryDomain] = useState('');
  const [pitchInventoryDomains, setPitchInventoryDomains] = useState<string[]>([]);
  const [expandedMatchGroups, setExpandedMatchGroups] = useState<Record<string, boolean>>({});
  const [showArchivedInventory, setShowArchivedInventory] = useState(false);
  const [publisherDetailOpen, setPublisherDetailOpen] = useState(false);
  const collapseInitializedRef = useRef(false);

  const queryClient = useQueryClient();
  const configQuery = useLeadOpsConfig();
  const historyQuery = useLeadOpsHistory();
  const checkSwMutation = useLeadOpsCheckSw();
  const checkMondayMutation = useLeadOpsCheckMonday();
  const trafficSourcesMutation = useLeadOpsTrafficSources();
  const competitorsMutation = useLeadOpsCompetitors();
  const activityLogMutation = useLeadOpsActivityLog();
  const createMutation = useLeadOpsCreate();
  const reassignMutation = useLeadOpsReassign();
  const markDuplicateMutation = useLeadOpsMarkDuplicate();
  const { username: currentUser } = useAuth();
  const { mode, changeMode, isPublisher, isUnsold } = useLeadOpsMode();
  const { open: onboardingOpen, finish: onboardingFinish } = useOnboarding();

  const isAnyCheckPending = checkSwMutation.isPending || checkMondayMutation.isPending;
  useEffect(() => {
    const hasData = hasCrmData || hasFullData;
    const showCrm = hasCrmData || (checkMondayMutation.isPending && (lastAction === 'monday' || lastAction === 'both'));
    const showSw = hasFullData || (checkSwMutation.isPending && (lastAction === 'sw' || lastAction === 'both')) || (isAnyCheckPending && lastAction === 'both');
    if (hasData || isAnyCheckPending) {
      if (!collapseInitializedRef.current) {
        collapseInitializedRef.current = true;
        setCollapseKeys([showCrm && 'crm', showSw && 'sw'].filter(Boolean) as string[]);
      } else {
        setCollapseKeys((prev) => prev.filter((k) => (k === 'crm' && showCrm) || (k === 'sw' && showSw)));
      }
    } else {
      collapseInitializedRef.current = false;
      setCollapseKeys([]);
    }
  }, [hasCrmData, hasFullData, isAnyCheckPending, lastAction, checkSwMutation.isPending, checkMondayMutation.isPending]);

  const handleLogout = () => {
    leadOpsApi.logout();
  };

  const handleReset = () => {
    setDomainInput('');
    setLastReadDomainTracked('');
    setLastDomainCanonical('');
    setCrmSnapshotTracked(null);
    setSwSnapshotTracked(null);
    setSuggestedActionsTracked(null);
    setHasFullData(false);
    setHasCrmData(false);
    setLastAction(null);
    setCollapseKeys([]);
    collapseInitializedRef.current = false;
    trafficSourcesMutation.reset();
    competitorsMutation.reset();
    setPitchInventoryMatch(null);
    setPitchInventoryDomain('');
    setPitchInventoryDomains([]);
    setExpandedMatchGroups({});
    setShowArchivedInventory(false);
    setPublisherDetailOpen(false);
    clearWorkbenchState();
    message.info('Cleared');
  };

  const crmStatus = crmSnapshot?.status;
  const matches = crmSnapshot?.matches || [];
  const qualifiedPartnerFound = Boolean(crmSnapshot?.qualified_partner_found);
  const qualifiedPartnerMatch = crmSnapshot?.qualified_partner_match || null;
  const qualifiedPartnerLead = qualifiedPartnerMatch?.item_id
    ? matches.find((m: LeadOpsMatch) => String(m.item_id) === String(qualifiedPartnerMatch.item_id))
    : null;
  const inactiveDays = suggestedActions?.inactive_days;
  const inactivityThreshold = configQuery.data?.data?.inactivity_days || 60;
  const leadsBoardId = String(configQuery.data?.data?.monday?.board_id || '').trim();
  const hasMultipleMatches = matches.length > 1;
  const isLeadsBoard = (m: { board_id?: string; board_name?: string }) => matchIsLeadsBoard(m, leadsBoardId);
  const isExactDomainMatch = (m: any) =>
    m ? matchIsExactDomainMatch(m, lastDomainCanonical) : false;
  const leadsMatches = matches
    .filter((m: any) => isLeadsBoard(m))
    .sort((a: any, b: any) => {
      const exactDifference = Number(!isExactDomainMatch(a)) - Number(!isExactDomainMatch(b));
      if (exactDifference !== 0) return exactDifference;
      const ra = rankLeadMatch(a, leadsBoardId, inactivityThreshold);
      const rb = rankLeadMatch(b, leadsBoardId, inactivityThreshold);
      for (let i = 0; i < ra.length; i++) {
        if (ra[i] !== rb[i]) return ra[i] - rb[i];
      }
      return 0;
    });
  const visibleGroupMatches = (group: LeadOpsMatch[], key: string, limit: number, preserveAllExact = false) => {
    if (expandedMatchGroups[key]) return group;
    const exact = group.filter(isExactDomainMatch);
    const remaining = group.filter((match) => !isExactDomainMatch(match));
    return [...exact, ...remaining].slice(0, preserveAllExact ? Math.max(limit, exact.length) : limit);
  };
  const toggleMatchGroup = (key: string) => {
    setExpandedMatchGroups((current) => ({ ...current, [key]: !current[key] }));
  };
  const isRelatedDomain = (m: LeadOpsMatch) => matchIsRelatedDomain(m, lastDomainCanonical);
  const primaryLeadsMatch =
    leadsMatches[0] ||
    (crmSnapshot?.best_match && isLeadsBoard(crmSnapshot.best_match) ? crmSnapshot.best_match : null);
  const showLeadCard = primaryLeadsMatch != null;
  const hasExactLeadMatch = leadsMatches.some((match: LeadOpsMatch) => isExactDomainMatch(match));
  // Backend owns the policy; the local exact/partner checks keep stale payloads fail-closed.
  const showCreateButton =
    isPublisher
    && Boolean(suggestedActions?.can_create)
    && !hasExactLeadMatch
    && !qualifiedPartnerFound;
  const isPrimaryQualified = !!primaryLeadsMatch && isMatchQualified(primaryLeadsMatch);
  const isPrimaryActivityPending = Boolean(primaryLeadsMatch?.activity_pending) || activityLogMutation.isPending;
  const isStale =
    crmStatus === 'FOUND'
    && typeof inactiveDays === 'number'
    && inactiveDays >= inactivityThreshold;
  const primaryIsExact = Boolean(primaryLeadsMatch && isExactDomainMatch(primaryLeadsMatch));
  const primaryStage = primaryLeadsMatch?.lead_status || primaryLeadsMatch?.group_name;
  const primaryGroupDiffers = Boolean(
    primaryLeadsMatch?.group_name
    && primaryLeadsMatch?.lead_status
    && primaryLeadsMatch.group_name.trim().toLowerCase() !== primaryLeadsMatch.lead_status.trim().toLowerCase()
  );
  const showPrimaryStale = isStale && !isPrimaryQualified;

  const mergeActivityIntoCrm = (snapshot: any, activityData: any) => {
    if (!snapshot || !activityData?.item_id || !activityData?.board_id) return snapshot;
    const sameMatch = (m: any) =>
      String(m?.item_id || '') === String(activityData.item_id)
      && String(m?.board_id || '') === String(activityData.board_id);
    const enrich = (m: any) => sameMatch(m)
      ? {
          ...m,
          activity_log: activityData.activity_log || [],
          activity_pending: false,
          last_activity_at: activityData.last_activity_at || m.last_activity_at,
        }
      : m;
    return {
      ...snapshot,
      best_match: snapshot.best_match ? enrich(snapshot.best_match) : snapshot.best_match,
      matches: (snapshot.matches || []).map(enrich),
    };
  };

  const loadBestActivity = (domain: string, crm: any, actions: any, domainCanonical?: string) => {
    const best = crm?.best_match;
    const partnerBlocked = Boolean(crm?.qualified_partner_found);
    if (!best?.item_id || !best?.board_id || !isLeadsBoard(best) || partnerBlocked) return;
    activityLogMutation.mutate(
      { item_id: String(best.item_id), board_id: String(best.board_id), limit: 50 },
      {
        onSuccess: (response) => {
          if (domain.toLowerCase() !== lastReadDomainRef.current.toLowerCase()) return;
          const activityData = response?.data;
          const updatedCrm = mergeActivityIntoCrm(crmSnapshotRef.current || crm, activityData);
          const activityEligibility = resolveActivityReassign(
            activityData?.can_reassign,
            activityData?.reassign_reason,
            partnerBlocked,
          );
          const nextSuggestedActions = {
            ...(suggestedActionsRef.current || actions || {}),
            ...activityEligibility,
            inactive_days: activityData?.inactive_days ?? null,
          };
          setCrmSnapshotTracked(updatedCrm);
          setSuggestedActionsTracked(nextSuggestedActions);
          saveWorkbenchState(domain, {
            domain_canonical: domainCanonical || domain,
            similarweb: swSnapshotRef.current,
            crm: updatedCrm,
            suggested_actions: nextSuggestedActions,
          });
        },
        onError: () => {
          if (domain.toLowerCase() !== lastReadDomainRef.current.toLowerCase()) return;
          const nextSuggestedActions = {
            ...(suggestedActionsRef.current || actions || {}),
            can_reassign: false,
            reassign_reason: 'Sales activity is unavailable; manual review required',
          };
          setSuggestedActionsTracked(nextSuggestedActions);
        },
      }
    );
  };

  const formatSummaryValue = (key: string, value: unknown) => {
    if (value === null || value === undefined) {
      return '-';
    }
    if (key === 'date_range' && value && typeof value === 'object') {
      const range = value as { start_date?: string; end_date?: string };
      if (range.start_date || range.end_date) {
        return `${range.start_date || '-'} → ${range.end_date || '-'}`;
      }
    }
    if (key === 'top_countries' && Array.isArray(value)) {
      return value
        .map((entry) => {
          if (!entry || typeof entry !== 'object') return 'Unknown';
          const country = (entry as { country?: { name?: string; alpha2?: string } }).country || {};
          const name = country.name || 'Unknown';
          const share = (entry as { share?: number }).share;
          const shareText = typeof share === 'number' ? ` ${formatPercent(share * 100)}` : '';
          return `${name}${shareText}`;
        })
        .join(', ');
    }
    if (key === 'bounce_rate' && typeof value === 'number') {
      return formatPercent(value * 100);
    }
    if ((key === 'desktop_share' || key === 'mobile_share') && typeof value === 'number') {
      return formatPercent(value * 100);
    }
    if (key === 'avg_visit_duration' && typeof value === 'number') {
      return formatDuration(value);
    }
    if (key === 'unique_visitors' && typeof value === 'number') {
      return formatNumber(value);
    }
    if (key === 'visits' && typeof value === 'number') {
      return formatNumber(value);
    }
    if (key === 'pages_per_visit' && typeof value === 'number') {
      return formatNumber(value);
    }
    if (typeof value === 'number') {
      return formatNumber(value);
    }
    if (Array.isArray(value) || (value && typeof value === 'object')) {
      return JSON.stringify(value);
    }
    return String(value);
  };

  const clearCardsIfDomainChanged = (newDomain: string) => {
    const prev = lastReadDomain.trim().toLowerCase();
    const next = newDomain.trim().toLowerCase();
    if (prev && prev !== next) {
      setSwSnapshotTracked(null);
      setCrmSnapshotTracked(null);
      setSuggestedActionsTracked(null);
      setHasFullData(false);
      setHasCrmData(false);
      trafficSourcesMutation.reset();
      competitorsMutation.reset();
      setPitchInventoryMatch(null);
      setPitchInventoryDomain('');
      setPitchInventoryDomains([]);
    }
  };

  const handleCheckSw = (domainOverride?: string, mode: 'full' | 'light' = 'full') => {
    const domain = (domainOverride ?? domainInput).trim();
    if (!domain) {
      message.warning('Enter a domain');
      return;
    }
    clearCardsIfDomainChanged(domain);
    setLastReadDomainTracked(domain);
    setLastAction('sw');
    checkSwMutation.mutate(
      { domain, mode },
      {
        onSuccess: (response) => {
          if (domain.toLowerCase() !== lastReadDomainRef.current.toLowerCase()) return;
          const data = response?.data;
          setSwSnapshotTracked(data?.similarweb || null);
          setLastDomainCanonical(data?.domain_canonical || domain);
          setHasFullData(Boolean(data?.similarweb));
          saveWorkbenchState(domain, {
            domain_canonical: data?.domain_canonical || domain,
            similarweb: data?.similarweb || null,
            crm: crmSnapshotRef.current,
            suggested_actions: suggestedActionsRef.current,
          });
          queryClient.invalidateQueries({ queryKey: ['lead-ops', 'history'] });
        },
        onError: (err: any) => {
          const msg = err?.response?.data?.error || err?.message || 'Failed to check Similarweb';
          message.error(msg);
        },
      }
    );
  };

  const handleCheckMonday = (domainOverride?: string) => {
    const domain = (domainOverride ?? domainInput).trim();
    if (!domain) {
      message.warning('Enter a domain');
      return;
    }
    clearCardsIfDomainChanged(domain);
    setLastReadDomainTracked(domain);
    setLastAction('monday');
    checkMondayMutation.mutate(domain, {
      onSuccess: (response) => {
        if (domain.toLowerCase() !== lastReadDomainRef.current.toLowerCase()) return;
        const data = response?.data;
        const newCanonical = data?.domain_canonical || domain;
        const currentSw = swSnapshotRef.current;
        const swIsForDifferentDomain = currentSw != null && lastDomainCanonical != null && !domainsAreRelated(newCanonical, lastDomainCanonical);
        if (swIsForDifferentDomain) {
          setSwSnapshotTracked(null);
          setHasFullData(false);
        }
        setCrmSnapshotTracked(data?.crm || null);
        setSuggestedActionsTracked(data?.suggested_actions || null);
        setLastDomainCanonical(data?.domain_canonical || domain);
        setHasCrmData(Boolean(data?.crm));
        saveWorkbenchState(domain, {
          domain_canonical: data?.domain_canonical || domain,
          similarweb: swIsForDifferentDomain ? null : currentSw,
          crm: data?.crm || null,
          suggested_actions: data?.suggested_actions || null,
        });
        loadBestActivity(domain, data?.crm, data?.suggested_actions, data?.domain_canonical || domain);
        queryClient.invalidateQueries({ queryKey: ['lead-ops', 'history'] });
      },
      onError: (err: any) => {
        const msg = err?.response?.data?.error || err?.message || 'Failed to check Monday';
        message.error(msg);
      },
    });
  };

  const handleCheckBoth = async (domainOverride?: string) => {
    const domain = (domainOverride ?? domainInput).trim();
    if (!domain) {
      message.warning('Enter a domain');
      return;
    }
    const sameDomain = domain.toLowerCase() === lastReadDomainRef.current.trim().toLowerCase();
    clearCardsIfDomainChanged(domain);
    setLastReadDomainTracked(domain);
    setLastAction('both');
    const [swResult, mondayResult] = await Promise.allSettled([
      checkSwMutation.mutateAsync({ domain, mode: 'full' }),
      checkMondayMutation.mutateAsync(domain),
    ]);
    if (domain.toLowerCase() !== lastReadDomainRef.current.toLowerCase()) return;

    const swData = swResult.status === 'fulfilled' ? swResult.value?.data : null;
    const mondayData = mondayResult.status === 'fulfilled' ? mondayResult.value?.data : null;
    const merged = {
      domain_input: domain,
      domain_canonical: swData?.domain_canonical || mondayData?.domain_canonical || domain,
      similarweb: swData?.similarweb || (sameDomain ? swSnapshotRef.current : null),
      crm: mondayData?.crm || (sameDomain ? crmSnapshotRef.current : null),
      suggested_actions: mondayData?.suggested_actions || (sameDomain ? suggestedActionsRef.current : null),
    };

    setSwSnapshotTracked(merged.similarweb);
    setCrmSnapshotTracked(merged.crm);
    setSuggestedActionsTracked(merged.suggested_actions);
    setLastDomainCanonical(merged.domain_canonical);
    setHasFullData(Boolean(merged.similarweb));
    setHasCrmData(Boolean(merged.crm));
    saveWorkbenchState(domain, merged);
    if (mondayData?.crm) {
      loadBestActivity(domain, mondayData.crm, mondayData.suggested_actions, merged.domain_canonical);
    }
    queryClient.invalidateQueries({ queryKey: ['lead-ops', 'history'] });

    if (swResult.status === 'rejected') {
      const err: any = swResult.reason;
      message.warning(`Similarweb: ${err?.response?.data?.error || err?.message || 'unavailable'}`);
    }
    if (mondayResult.status === 'rejected') {
      const err: any = mondayResult.reason;
      message.warning(`Monday: ${err?.response?.data?.error || err?.message || 'unavailable'}`);
    }
  };

  const isCheckBothPending = checkSwMutation.isPending || checkMondayMutation.isPending;

  const handleTrafficSources = () => {
    if (!lastReadDomain) {
      message.warning('Run Read first');
      return;
    }
    trafficSourcesMutation.mutate(lastReadDomain);
  };

  const pitchDomainForMatch = (match: any) => {
    const fromMatch = String(match?.domain_canonical || match?.domain || '').trim();
    if (fromMatch) return fromMatch;
    const name = String(match?.name || '').trim();
    const firstPart = name.split(' - ')[0]?.trim();
    return firstPart || lastDomainCanonical || domainInput.trim();
  };

  const resolvePitchDomains = (match: any): string[] =>
    resolveInventoryPitchDomains(match, pitchDomainForMatch(match));

  const pickDefaultPitchDomain = (domains: string[]): string => {
    if (domains.length === 0) return '';
    const search = (lastDomainCanonical || domainInput).trim().toLowerCase();
    if (search) {
      const exact = domains.find((item) => item === search);
      if (exact) return exact;
      const related = domains.find((item) => domainsAreRelated(item, search));
      if (related) return related;
    }
    return domains[0];
  };

  const ensureSimilarwebForPitch = (domain: string) => {
    const normalized = domain.trim();
    if (!normalized || checkSwMutation.isPending) return;
    const summary = swSnapshotRef.current?.summary;
    const hasSummary = Boolean(summary && !summary.error);
    const sameDomain = domainsAreRelated(normalized, lastDomainCanonical || normalized);
    if (hasSummary && sameDomain) return;
    checkSwMutation.mutate(
      { domain: normalized, mode: 'full' },
      {
        onSuccess: (response) => {
          const data = response?.data;
          setSwSnapshotTracked(data?.similarweb || null);
          setLastDomainCanonical(data?.domain_canonical || normalized);
          setHasFullData(Boolean(data?.similarweb));
          saveWorkbenchState(normalized, {
            domain_canonical: data?.domain_canonical || normalized,
            similarweb: data?.similarweb || null,
            crm: crmSnapshotRef.current,
            suggested_actions: suggestedActionsRef.current,
          });
        },
      },
    );
  };

  const ensureTrafficSourcesForPitch = (domain: string) => {
    const normalized = domain.trim();
    if (!normalized || trafficSourcesMutation.isPending) return;
    const loadedDomain = String(trafficSourcesMutation.variables || '').trim().toLowerCase();
    if (
      trafficSourcesMutation.data?.data
      && loadedDomain
      && domainsAreRelated(normalized, loadedDomain)
    ) {
      return;
    }
    trafficSourcesMutation.mutate(normalized);
  };

  const handleOpenInventoryPitch = (match: any) => {
    const domains = resolvePitchDomains(match);
    const selected = pickDefaultPitchDomain(domains);
    setPitchInventoryMatch(match);
    setPitchInventoryDomains(domains);
    setPitchInventoryDomain(selected);
    if (selected) {
      ensureSimilarwebForPitch(selected);
      ensureTrafficSourcesForPitch(selected);
    }
  };

  const handleInventoryPitchDomainChange = (nextDomain: string) => {
    const normalized = nextDomain.trim().toLowerCase();
    if (!normalized || normalized === pitchInventoryDomain) return;
    setPitchInventoryDomain(normalized);
    ensureSimilarwebForPitch(normalized);
    ensureTrafficSourcesForPitch(normalized);
  };

  const handleCompetitors = () => {
    if (!lastReadDomain) {
      message.warning('Run Read first');
      return;
    }
    competitorsMutation.mutate(lastReadDomain);
  };

  const doCreate = () => {
    createMutation.mutate(
      {
        domain_canonical: lastDomainCanonical,
        sw_snapshot: {
          summary: swSnapshot?.summary ?? null,
          traffic_sources: trafficSourcesMutation.data?.data ?? null,
          competitors: competitorsMutation.data?.data ?? null,
        },
      },
      {
        onSuccess: (response) => {
          message.success('Lead created');
          if (response?.data?.url) {
            window.open(response.data.url, '_blank', 'noopener,noreferrer');
          }
          if (lastReadDomain) {
            handleCheckBoth(lastReadDomain);
          }
        },
        onError: (err: any) => {
          const msg = err?.response?.data?.error || err?.message || 'Failed to create lead';
          message.error(msg);
        },
      }
    );
  };

  const handleCreate = () => {
    if (!lastDomainCanonical) {
      message.warning('Check the domain first');
      return;
    }
    if (!lastDomainCanonical.includes('.')) {
      message.warning('Domain must include a TLD (e.g. example.com)');
      return;
    }
    if (!swSnapshot?.summary || swSnapshot.summary.error) {
      Modal.confirm({
        title: 'Create without Similarweb data?',
        content: 'No Similarweb data loaded. The card will be created without a traffic snapshot.',
        okText: 'Create anyway',
        cancelText: 'Cancel',
        onOk: doCreate,
      });
    } else {
      doCreate();
    }
  };

  const handleReassign = (override?: { item_id: string; board_id?: string }) => {
    if (qualifiedPartnerFound) {
      message.warning('Qualified partner — reassignment is blocked');
      return;
    }
    const itemId = override?.item_id ?? primaryLeadsMatch?.item_id ?? crmSnapshot?.best_match?.item_id;
    const boardId = override?.board_id ?? primaryLeadsMatch?.board_id ?? crmSnapshot?.best_match?.board_id;
    if (!itemId) return;
    Modal.confirm({
      title: 'Reassign this lead to you?',
      content: 'The lead will be assigned to you and its status will change to Contacted.',
      okText: 'Reassign',
      cancelText: 'Cancel',
      onOk: () => {
        setPendingReassignId(String(itemId));
        reassignMutation.mutate(
          { item_id: String(itemId), board_id: boardId ? String(boardId) : undefined },
          {
            onSuccess: () => {
              setPendingReassignId(null);
              message.success('Lead reassigned');
              if (lastReadDomain) {
                handleCheckBoth(lastReadDomain);
              }
            },
            onError: (err: any) => {
              setPendingReassignId(null);
              const msg = err?.response?.data?.error || err?.message || 'Failed to reassign lead';
              message.error(msg);
            },
          }
        );
      },
    });
  };

  const handleMarkAsDuplicate = (match: { item_id: string; board_id?: string; name?: string; domain?: string }) => {
    const itemId = match.item_id;
    const boardId = match.board_id;
    if (!itemId || !boardId) return;
    const currentName = match.name || match.domain || '';
    setPendingDuplicateId(String(itemId));
    markDuplicateMutation.mutate(
      { item_id: String(itemId), board_id: String(boardId), current_name: currentName },
      {
        onSuccess: () => {
          setPendingDuplicateId(null);
          message.success('Marked as duplicate');
          const domain = (lastReadDomain || domainInput || lastDomainCanonical).trim();
          if (domain) {
            handleCheckBoth(domain);
          }
        },
        onError: () => {
          setPendingDuplicateId(null);
          message.error('Failed to mark as duplicate');
        },
      }
    );
  };

  const trafficSourcesOverview = trafficSourcesMutation.data?.data?.overview || [];
  const trafficSourcesByType = trafficSourcesOverview.reduce((acc: Record<string, number>, item: any) => {
    const label = item.source_type || item.source || 'Unknown';
    const rawValue = item.value ?? item.share ?? 0;
    const value = typeof rawValue === 'number' ? (rawValue <= 1 ? rawValue * 100 : rawValue) : 0;
    acc[label] = Math.max(acc[label] || 0, value);
    return acc;
  }, {});
  const trafficSources: Array<{ label: string; value: number }> = Object.keys(trafficSourcesByType)
    .map((label) => ({ label, value: trafficSourcesByType[label] ?? 0 }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 5);

  const competitors = (
    competitorsMutation.data?.data?.similar_sites
    || competitorsMutation.data?.data?.top_sites
    || []
  ).slice(0, 5);

  const visits = swSnapshot?.summary?.visits;
  const visitsMomPct = swSnapshot?.summary?.visits_mom_pct;
  const visitsPrevMonth = swSnapshot?.summary?.visits_prev_month;
  const uniqueVisitors = swSnapshot?.summary?.unique_visitors;
  const bounceRate = swSnapshot?.summary?.bounce_rate;
  const avgDuration = swSnapshot?.summary?.avg_visit_duration;
  const pagesPerVisit = swSnapshot?.summary?.pages_per_visit;
  const desktopShare = swSnapshot?.summary?.desktop_share;
  const mobileShare = swSnapshot?.summary?.mobile_share;
  const topCountries = swSnapshot?.summary?.top_countries || [];
  const geoCountries = topCountries.slice(0, 10);
  const swWarnings = swSnapshot?.summary?.warnings || [];

  const handleCopyTopGeos = async () => {
    try {
      await copyTextToClipboard(buildTopGeosLine(geoCountries));
      message.success('Top GEO copied');
    } catch {
      message.error('Could not copy Top GEO');
    }
  };

  const bounceColor = typeof bounceRate === 'number'
    ? bounceRate < 0.4 ? 'green' : bounceRate > 0.6 ? 'orange' : 'default'
    : 'default';
  const durationColor = typeof avgDuration === 'number'
    ? avgDuration > 180 ? 'green' : avgDuration < 60 ? 'orange' : 'default'
    : 'default';
  const isCrmLoading = (checkMondayMutation.isPending && lastAction === 'monday') || (isCheckBothPending && lastAction === 'both');
  const isSwLoading = (checkSwMutation.isPending && lastAction === 'sw') || (isCheckBothPending && lastAction === 'both');
  const showCrmCard = hasCrmData || isCrmLoading;
  const showSwCard = hasFullData || isSwLoading;
  const hasAnyData = showCrmCard || showSwCard;
  const swError = swSnapshot?.error || swSnapshot?.summary?.error;
  const allInventoryMatches = matches.filter(
    (match: LeadOpsMatch) => (match.board_name || '').trim() === 'Inventory',
  );
  const archivedInventoryMatches = allInventoryMatches.filter(isArchivedInventory);
  const liveInventoryMatches = allInventoryMatches.filter((match: LeadOpsMatch) => !isArchivedInventory(match));
  const advertiserInventoryMatches = sortInventoryForAdvertiser(
    showArchivedInventory ? allInventoryMatches : liveInventoryMatches,
  );
  const websiteMatchesList = matches.filter(
    (match: LeadOpsMatch) => (match.board_name || '').trim() !== 'Inventory' && !isLeadsBoard(match),
  );
  const otherLeadsCount = Math.max(0, leadsMatches.length - (primaryLeadsMatch ? 1 : 0));
  const showPublisherSummary = isPublisher && !publisherDetailOpen;
  const showDetailedCrm = !showPublisherSummary;


                const crmPanel = showCrmCard ? {
                key: 'crm' as const,
                label: (
                  <div className="leadops-crm-panel-label">
                    <span className="leadops-crm-panel-title">
                      CRM{lastDomainCanonical ? ` · ${lastDomainCanonical}` : ''}
                    </span>
                    <Tag
                      color={
                        crmStatus === 'FOUND'
                          ? 'green'
                          : crmStatus === 'NOT_FOUND'
                            ? 'blue'
                            : crmStatus === 'AMBIGUOUS'
                              ? 'orange'
                              : 'default'
                      }
                      style={{ margin: 0 }}
                    >
                      {crmStatus || '—'}
                    </Tag>
                  </div>
                ),
                children: (
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    {isCrmLoading && !hasCrmData ? (
                      <Skeleton active paragraph={{ rows: 4 }} />
                    ) : (
                    <>
                    {showPublisherSummary && (
                      <PublisherSummary
                        primaryLeadMatch={primaryLeadsMatch}
                        primaryIsExact={primaryIsExact}
                        otherLeadsCount={otherLeadsCount}
                        websiteMatches={websiteMatchesList}
                        inventoryMatches={allInventoryMatches}
                        isExactDomainMatch={isExactDomainMatch}
                      />
                    )}
                    {showDetailedCrm && showLeadCard && primaryLeadsMatch && (
                      <div className="leadops-primary-card">
                        <div className="leadops-primary-header">
                          <div className="leadops-primary-identity">
                            <Text strong className="leadops-primary-name">{primaryLeadsMatch.domain || primaryLeadsMatch.name}</Text>
                            <Tag color={primaryIsExact ? 'green' : 'default'}>{primaryIsExact ? 'Exact' : 'Related'}</Tag>
                          </div>
                          <div className="leadops-primary-actions">
                            {primaryLeadsMatch.url && (
                              <Button
                                aria-label="Open"
                                size="small"
                                icon={<LinkOutlined />}
                                onClick={() => window.open(primaryLeadsMatch.url, '_blank', 'noopener,noreferrer')}
                              >
                                Open
                              </Button>
                            )}
                            <Tooltip
                              title={
                                !hasCrmData
                                  ? 'Check CRM first'
                                  : qualifiedPartnerFound || isPrimaryQualified
                                    ? 'Lead is already a qualified partner'
                                    : isPrimaryActivityPending
                                      ? 'Checking sales activity before reassignment'
                                    : !suggestedActions?.can_reassign
                                      ? (suggestedActions?.reassign_reason
                                          || `Lead must be inactive ${inactivityThreshold}+ days to reassign${typeof inactiveDays === 'number' ? ` (currently ${inactiveDays}d)` : ''}`)
                                      : undefined
                              }
                            >
                              <span>
                                <Button
                                  aria-label="Reassign to me"
                                  size="small"
                                  type={isStale && !isPrimaryQualified && !qualifiedPartnerFound ? 'primary' : 'default'}
                                  icon={<UserSwitchOutlined />}
                                  onClick={() => handleReassign()}
                                  disabled={!hasCrmData || qualifiedPartnerFound || isPrimaryQualified || !suggestedActions?.can_reassign}
                                  loading={pendingReassignId === String(primaryLeadsMatch?.item_id) || isPrimaryActivityPending}
                                >
                                  Reassign to me
                                </Button>
                              </span>
                            </Tooltip>
                          </div>
                        </div>
                        {crmStatus === 'FOUND' && (
                          <>
                            <div className="leadops-meta-grid">
                              <div className="leadops-meta-item">
                                <Text type="secondary">Manager</Text>
                                <div className="leadops-meta-value"><Text>{primaryLeadsMatch.owner_name || '—'}</Text></div>
                              </div>
                              {primaryStage && (
                                <div className="leadops-meta-item">
                                  <Text type="secondary">Stage</Text>
                                  <div className="leadops-meta-value"><Text>{primaryStage}</Text></div>
                                </div>
                              )}
                              {primaryGroupDiffers && (
                                <div className="leadops-meta-item">
                                  <Text type="secondary">Group</Text>
                                  <div className="leadops-meta-value"><Text>{primaryLeadsMatch.group_name}</Text></div>
                                </div>
                              )}
                              {typeof inactiveDays === 'number' && (
                                <div className="leadops-meta-item">
                                  <Text type="secondary">Last activity</Text>
                                  <div className={`leadops-meta-value${showPrimaryStale ? ' leadops-meta-value-stale' : ''}`}>
                                    <Text>{inactiveDays === 0 ? 'No updates yet' : `${inactiveDays}d ago${showPrimaryStale ? ' · stale' : ''}`}</Text>
                                  </div>
                                </div>
                              )}
                            </div>
                            {primaryLeadsMatch.activity_log?.length > 0 && (
                              <div className="leadops-primary-activity">
                                <Text type="secondary">Activity Log</Text>
                                <div style={{ marginTop: 6, maxHeight: 120, overflow: 'auto', fontSize: 12 }}>
                                  {primaryLeadsMatch.activity_log.slice(0, 5).map((u: any, i: number) => (
                                    <div key={i} style={{ marginBottom: 6, paddingBottom: 6, borderBottom: i < 4 ? '1px solid #f0f0f0' : 'none' }}>
                                      <Text type="secondary">{u.created_at ? formatHumanDateTime(u.created_at) : ''}</Text>
                                      <div><Text>{String(u.body || '').slice(0, 120)}{(u.body?.length || 0) > 120 ? '…' : ''}</Text></div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {showDetailedCrm && (hasMultipleMatches || (crmStatus === 'FOUND' && !showLeadCard)) && (
                      <div className="leadops-match-groups-shell">
                          {(() => {
                            const listMatches = showLeadCard
                              ? matches.filter((m: any) => m.item_id !== primaryLeadsMatch?.item_id || m.board_id !== primaryLeadsMatch?.board_id)
                              : matches;
                            const inventoryMatches = isPublisher
                              ? listMatches.filter((m: any) => m.board_name === 'Inventory')
                              : advertiserInventoryMatches;
                            const otherLeadsMatches = listMatches.filter((m: any) => isLeadsBoard(m));
                            const otherMatches = listMatches.filter((m: any) => m.board_name !== 'Inventory' && !isLeadsBoard(m));
                            const visibleLeadsMatches = visibleGroupMatches(otherLeadsMatches, 'leads', 3, true);
                            const visibleWebsiteMatches = visibleGroupMatches(otherMatches, 'websites', 3);
                            const visibleInventoryMatches = visibleGroupMatches(inventoryMatches, 'inventory', 5);
                            const allLeadsWithRelatedDomain = [
                              ...(showLeadCard && primaryLeadsMatch && isRelatedDomain(primaryLeadsMatch) ? [primaryLeadsMatch] : []),
                              ...otherLeadsMatches.filter(isRelatedDomain),
                            ];
                            const hasMultipleRelated = allLeadsWithRelatedDomain.length >= 2;
                            const renderLeadCard = (match: any, isDuplicate: boolean) => {
                              const logEntries = match.activity_log?.slice(0, 5) || [];
                              const firstValid = logEntries.find((u: any) => u?.created_at)?.created_at;
                              const lastValid = [...logEntries].reverse().find((u: any) => u?.created_at)?.created_at;
                              const lastActivity = match.last_activity_at || firstValid || lastValid;
                              // Берём самую недавнюю дату (min daysSince = most recent = correct last activity)
                              const d1 = firstValid ? daysSince(firstValid) : null;
                              const d2 = lastValid ? daysSince(lastValid) : null;
                              const d3 = match.last_activity_at ? daysSince(match.last_activity_at) : null;
                              const validDays = [d1, d2, d3].filter((x): x is number => x != null && x >= 0);
                              const matchInactiveDays = validDays.length > 0 ? Math.min(...validDays) : null;
                              const matchCanReassign = !qualifiedPartnerFound && !isMatchQualified(match) && matchInactiveDays != null && matchInactiveDays >= inactivityThreshold;
                              const showMatchReassign = qualifiedPartnerFound || matchCanReassign;
                              const isAlreadyMarked = (match.name || '').toLowerCase().includes('duplicate');
                              const matchStage = match.lead_status || match.group_name;
                              const matchGroupDiffers = Boolean(
                                match.group_name
                                && match.lead_status
                                && String(match.group_name).trim().toLowerCase() !== String(match.lead_status).trim().toLowerCase()
                              );
                              return (
                                <div
                                  key={`lead-${match.board_id}-${match.item_id}`}
                                  className="leadops-match-card"
                                >
                                  <div className="leadops-match-header">
                                    <div className="leadops-match-main">
                                      <Text strong className="leadops-match-name">{match.domain || match.name}</Text>
                                      {isExactDomainMatch(match) && <Tag color="green">Exact</Tag>}
                                    </div>
                                    <div className="leadops-match-actions">
                                      {match.url && (
                                        <Tooltip title="Open">
                                        <Button aria-label="Open" size="small" icon={<LinkOutlined />} onClick={() => window.open(match.url, '_blank', 'noopener,noreferrer')}>
                                          Open
                                        </Button>
                                        </Tooltip>
                                      )}
                                      {showMatchReassign && (
                                        <Tooltip title={qualifiedPartnerFound ? 'Qualified partner — reassignment is blocked' : 'Reassign to me'}>
                                        <span>
                                          <Button
                                            aria-label="Reassign to me"
                                            size="small"
                                            type={qualifiedPartnerFound ? 'default' : 'primary'}
                                            icon={<UserSwitchOutlined />}
                                            onClick={() => handleReassign({ item_id: match.item_id, board_id: match.board_id })}
                                            loading={pendingReassignId === String(match.item_id)}
                                            disabled={qualifiedPartnerFound}
                                          >
                                            Reassign to me
                                          </Button>
                                        </span>
                                        </Tooltip>
                                      )}
                                    </div>
                                  </div>
                                      {isDuplicate && (
                                        <div
                                          style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 8,
                                            padding: '4px 10px',
                                            borderRadius: 8,
                                            border: '1px solid rgba(250, 173, 20, 0.5)',
                                            background: 'rgba(250, 173, 20, 0.08)',
                                          }}
                                        >
                                          <Tag color="orange">Duplicate?</Tag>
                                        {!isMatchQualified(match) && !isAlreadyMarked && (
                                          <Button size="small" onClick={() => handleMarkAsDuplicate(match)} loading={pendingDuplicateId === String(match.item_id)}>
                                            Mark as Duplicate
                                          </Button>
                                        )}
                                        </div>
                                      )}
                                  <div className="leadops-meta-grid">
                                    <div className="leadops-meta-item">
                                      <Text type="secondary">Manager</Text>
                                      <div className="leadops-meta-value"><Text>{match.owner_name || '—'}</Text></div>
                                    </div>
                                    {matchStage && (
                                      <div className="leadops-meta-item">
                                        <Text type="secondary">Stage</Text>
                                        <div className="leadops-meta-value"><Text>{matchStage}</Text></div>
                                      </div>
                                    )}
                                    {matchGroupDiffers && (
                                      <div className="leadops-meta-item">
                                        <Text type="secondary">Group</Text>
                                        <div className="leadops-meta-value"><Text>{match.group_name}</Text></div>
                                      </div>
                                    )}
                                    {(match.activity_log?.length > 0 || matchInactiveDays != null) && (
                                      <div className="leadops-meta-item">
                                        <Text type="secondary">Last activity</Text>
                                        <div className="leadops-meta-value">
                                          <Text>
                                            {lastActivity
                                              ? formatHumanDateTime(lastActivity)
                                              : matchInactiveDays != null
                                                ? `${matchInactiveDays}d ago`
                                                : '—'}
                                          </Text>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  {match.activity_log?.length > 0 ? (
                                    <div style={{ marginTop: 12 }}>
                                      <Text type="secondary">Activity Log</Text>
                                      <div style={{ marginTop: 6, maxHeight: 100, overflow: 'auto', fontSize: 12 }}>
                                        {match.activity_log.slice(0, 5).map((u: any, i: number) => (
                                          <div key={i} style={{ marginBottom: 6, paddingBottom: 6, borderBottom: i < 4 ? '1px solid #f0f0f0' : 'none' }}>
                                            <Text type="secondary">{u.created_at ? formatHumanDateTime(u.created_at) : ''}</Text>
                                            <div><Text>{String(u.body || '').slice(0, 120)}{(u.body?.length || 0) > 120 ? '…' : ''}</Text></div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  ) : null}
                                </div>
                              );
                            };
                            return (
                              <div className="leadops-match-groups">
                                {otherLeadsMatches.length > 0 && (
                                  <section className="leadops-match-group">
                                    <div className="leadops-match-group-header">
                                      <Text strong>Leads <span className="leadops-match-group-count">{otherLeadsMatches.length}</span></Text>
                                      {visibleLeadsMatches.length < otherLeadsMatches.length && (
                                        <Button type="link" className="leadops-group-toggle" onClick={() => toggleMatchGroup('leads')}>Show all {otherLeadsMatches.length}</Button>
                                      )}
                                      {expandedMatchGroups.leads && otherLeadsMatches.length > 3 && (
                                        <Button type="link" className="leadops-group-toggle" onClick={() => toggleMatchGroup('leads')}>Show less</Button>
                                      )}
                                    </div>
                                    {visibleLeadsMatches.map((match: any) => renderLeadCard(match, isRelatedDomain(match) && hasMultipleRelated))}
                                  </section>
                                )}
                                {otherMatches.length > 0 && (
                                  <section className="leadops-match-group">
                                    <div className="leadops-match-group-header">
                                      <Text strong>Websites <span className="leadops-match-group-count">{otherMatches.length}</span></Text>
                                      {visibleWebsiteMatches.length < otherMatches.length && (
                                        <Button type="link" className="leadops-group-toggle" onClick={() => toggleMatchGroup('websites')}>Show all {otherMatches.length}</Button>
                                      )}
                                      {expandedMatchGroups.websites && otherMatches.length > 3 && (
                                        <Button type="link" className="leadops-group-toggle" onClick={() => toggleMatchGroup('websites')}>Show less</Button>
                                      )}
                                    </div>
                                {visibleWebsiteMatches.map((match: any) => {
                                  const isLeadsMatch = isLeadsBoard(match);
                                  const canReassign = isLeadsMatch && !isMatchQualified(match) && daysSince(match.last_activity_at) >= inactivityThreshold;
                                  return (
                                    <div
                                      key={`${match.board_id}-${match.item_id}`}
                                      className="leadops-match-row"
                                    >
                                        <div className="leadops-match-main">
                                            <Text className="leadops-match-name">{match.domain || match.name}</Text>
                                            {isExactDomainMatch(match) && <Tag color="green">Exact</Tag>}
                                        </div>
                                        <div className="leadops-match-actions">
                                            <Popover
                                              overlayClassName="leadops-match-preview-popover"
                                              title="Preview"
                                              content={<MatchPreview match={match} />}
                                              trigger={['hover', 'click']}
                                              mouseEnterDelay={0.2}
                                              placement="bottomRight"
                                            >
                                              <Button aria-label="Preview" size="small" icon={<EyeOutlined />} />
                                            </Popover>
                                            {match.url && (
                                              <Tooltip title="Open">
                                                <Button
                                                  size="small"
                                                  icon={<LinkOutlined />}
                                                  onClick={() => window.open(match.url, '_blank', 'noopener,noreferrer')}
                                                />
                                              </Tooltip>
                                            )}
                                            {canReassign && (
                                              <Button
                                                size="small"
                                                type="primary"
                                                icon={<UserSwitchOutlined />}
                                                onClick={() => handleReassign({ item_id: match.item_id, board_id: match.board_id })}
                                                loading={pendingReassignId === String(match.item_id)}
                                              >
                                                Reassign
                                              </Button>
                                            )}
                                        </div>
                                    </div>
                                  );
                                })}
                                  </section>
                                )}
                                {inventoryMatches.length > 0 && (
                                  <section className="leadops-match-group">
                                    <div className="leadops-match-group-header">
                                      <Text strong>Inventory <span className="leadops-match-group-count">{inventoryMatches.length}</span></Text>
                                      {!isPublisher && archivedInventoryMatches.length > 0 && (
                                        <Button
                                          size="small"
                                          onClick={() => setShowArchivedInventory((current) => !current)}
                                        >
                                          {showArchivedInventory
                                            ? 'Hide Finished/Failed'
                                            : `Show Finished/Failed (${archivedInventoryMatches.length})`}
                                        </Button>
                                      )}
                                    </div>
                                {visibleInventoryMatches.map((match: any) => {
                                  const inventoryStatus = getInventoryStatus(match);
                                  return (
                                    <div
                                      key={match.item_id}
                                      className="leadops-match-row"
                                    >
                                        <div className="leadops-match-main">
                                            <Text className="leadops-match-name">{match.domain || match.name}</Text>
                                            {inventoryStatus && (
                                              <Tag color={inventoryStatusColor(inventoryStatus)}>{inventoryStatus}</Tag>
                                            )}
                                        </div>
                                        <div className="leadops-match-actions">
                                            <Popover
                                              overlayClassName="leadops-match-preview-popover"
                                              title="Preview"
                                              content={<MatchPreview match={match} />}
                                              trigger={['hover', 'click']}
                                              mouseEnterDelay={0.2}
                                              placement="bottomRight"
                                            >
                                              <Button aria-label="Preview" size="small" icon={<EyeOutlined />} />
                                            </Popover>
                                            {match.url && (
                                              <Tooltip title="Open">
                                                <Button
                                                  size="small"
                                                  icon={<LinkOutlined />}
                                                  onClick={() => window.open(match.url, '_blank', 'noopener,noreferrer')}
                                                />
                                              </Tooltip>
                                            )}
                                            {!isPublisher && (
                                              <Tooltip title="Pitch to advertiser">
                                                <Button
                                                  aria-label="Pitch"
                                                  size="small"
                                                  icon={<ThunderboltOutlined />}
                                                  onClick={() => handleOpenInventoryPitch(match)}
                                                />
                                              </Tooltip>
                                            )}
                                        </div>
                                    </div>
                                  );
                                })}
                                    {inventoryMatches.length > 5 && (
                                      <div className="leadops-inventory-toggle-row">
                                        <Button
                                          block
                                          icon={expandedMatchGroups.inventory ? <UpOutlined /> : <DownOutlined />}
                                          onClick={() => toggleMatchGroup('inventory')}
                                        >
                                          {expandedMatchGroups.inventory
                                            ? 'Show less'
                                            : `Show all (${inventoryMatches.length})`}
                                        </Button>
                                      </div>
                                    )}
                                  </section>
                                )}
                              </div>
                            );
                          })()}
                      </div>
                    )}

                    {crmSnapshot?.error && <Alert type="warning" message={crmSnapshot.error} />}

                    {hasExactLeadMatch && (
                      <div className="leadops-exact-lead-notice">
                        <CheckCircleOutlined />
                        <div>
                          <Text strong>Exact Lead already exists</Text>
                          <Text type="secondary">Use the existing Lead card for this domain.</Text>
                        </div>
                      </div>
                    )}

                    {!hasExactLeadMatch && qualifiedPartnerFound && (
                      <div className="leadops-exact-lead-notice leadops-partner-notice">
                        <TrophyOutlined />
                        <div>
                          <Text strong>Qualified partner already owns this domain</Text>
                          <Text type="secondary">
                            {suggestedActions?.create_blocked_reason
                              || 'A qualified partner already owns this domain.'}
                            {' '}Use the existing Lead instead of creating a new one.
                          </Text>
                        </div>
                        {qualifiedPartnerLead?.url && (
                          <Button
                            size="small"
                            icon={<LinkOutlined />}
                            onClick={() => window.open(qualifiedPartnerLead.url, '_blank', 'noopener,noreferrer')}
                          >
                            Open Lead
                          </Button>
                        )}
                      </div>
                    )}

                    {showCreateButton && (
                      <div className="leadops-create-action">
                        <div className="leadops-create-copy">
                          <Text strong>No exact Lead found</Text>
                          <Text type="secondary">
                            {crmStatus === 'FOUND'
                              ? 'Website and Inventory matches are context only.'
                              : 'No CRM context was found for this domain.'}
                          </Text>
                        </div>
                        <Tooltip title={lastDomainCanonical && !lastDomainCanonical.includes('.') ? 'Domain must include a TLD (e.g. example.com)' : null}>
                          <span>
                            <Button
                              type="primary"
                              icon={<PlusOutlined />}
                              onClick={handleCreate}
                              disabled={!hasCrmData || !lastDomainCanonical?.includes('.')}
                              loading={createMutation.isPending}
                            >
                              Create Lead &amp; assign to me
                            </Button>
                          </span>
                        </Tooltip>
                      </div>
                    )}
                    </>
                    )}
                </Space>
                ),
              } : null;

  return (
    <div className="leadops-page">
      <header className="leadops-page-header">
        <div className="leadops-heading">
          <Title level={2} className="leadops-title">Lead Workbench</Title>
          <Text type="secondary" className="leadops-subtitle">Similarweb + Monday CRM</Text>
        </div>
        <div className="leadops-user-actions">
          {currentUser && (
            <Dropdown
              trigger={['click']}
              menu={{
                items: [{ key: 'logout', label: 'Log out', icon: <LogoutOutlined /> }],
                onClick: ({ key }) => key === 'logout' && handleLogout(),
              }}
            >
              <Button className="leadops-account-button" icon={<UserOutlined />}>
                <span className="leadops-account-email">{currentUser}</span>
                <DownOutlined />
              </Button>
            </Dropdown>
          )}
        </div>
      </header>

      <SearchBar
        domainInput={domainInput}
        onDomainChange={setDomainInput}
        onHistorySelect={(domain) => setDomainInput(domain)}
        historyItems={historyQuery.data?.data?.items ?? []}
        onCheckSw={() => handleCheckSw(undefined, 'full')}
        onCheckMonday={() => handleCheckMonday()}
        onCheckBoth={() => handleCheckBoth()}
        isLoadingSw={checkSwMutation.isPending}
        isLoadingMonday={checkMondayMutation.isPending}
        isLoadingBoth={isCheckBothPending}
        lastAction={lastAction}
        mode={mode}
        onModeChange={changeMode}
        showClear={hasAnyData}
        onClear={handleReset}
      />

      <OnboardingTour open={onboardingOpen} onClose={onboardingFinish} />

      {isUnsold ? (
        <>
          <Divider className="leadops-results-divider" />
          <UnsoldInventoryPanel onPitch={handleOpenInventoryPitch} />
        </>
      ) : (
        <>
      {isPublisher && crmPanel && (
        <Card
          className="leadops-pinned-crm"
          title={crmPanel.label}
          extra={
            <Button type="link" className="leadops-group-toggle" onClick={() => setPublisherDetailOpen((v) => !v)}>
              {publisherDetailOpen ? 'Hide full CRM detail' : 'Show full CRM detail'}
            </Button>
          }
        >
          {crmPanel.children}
        </Card>
      )}

      {(checkSwMutation.isError || checkMondayMutation.isError) && (
        <Alert
          style={{ marginTop: 16 }}
          type="error"
          message="Failed to check domain"
          description="Please verify backend connectivity and try again."
        />
      )}

      {(isPublisher ? showSwCard : hasAnyData) && (
        <>
          <Divider className="leadops-results-divider" />
          <Collapse
            className="leadops-results"
            activeKey={collapseKeys}
            onChange={(keys) => setCollapseKeys(Array.isArray(keys) ? keys : [keys])}
            style={{ marginTop: 0 }}
            items={(() => {
              const panels = [
                !isPublisher && crmPanel,
              showSwCard && {
                key: 'sw',
                label: (
                  <div className="leadops-panel-label">
                    <Space wrap>
                      <span>Traffic & Engagement</span>
                      {!swError && typeof visits === 'number' && visits >= 1000000 && (
                        <Tag color="blue">{formatNumber(visits / 1e6)}M visits</Tag>
                      )}
                    </Space>
                    {!swError && lastDomainCanonical && (
                      <div className="leadops-panel-actions">
                        <Button
                          size="small"
                          icon={<ExportOutlined />}
                          onClick={(event) => {
                            event.stopPropagation();
                            const url = `https://pro.similarweb.com/#/sales/account-review/overview/website-performance/${lastDomainCanonical}/*/999/3m?webSource=Total`;
                            window.open(url, '_blank', 'noopener,noreferrer');
                          }}
                          style={{
                            color: '#8c6b1a',
                            borderColor: '#d9d9d9',
                            backgroundColor: '#faf8f5',
                          }}
                        >
                          <span className="desktop-label">Open in Similarweb</span><span className="mobile-label">Similarweb</span>
                        </Button>
                      </div>
                    )}
                  </div>
                ),
                children: (
                  <>
                  {isSwLoading && <Skeleton active paragraph={{ rows: 4 }} />}
                  {!isSwLoading && swError && (
                    <Alert type="warning" message={swError} />
                  )}
                  {!isSwLoading && !swError && (
                    <>
                      <div className="leadops-metrics-grid">
                        <div className="leadops-metric leadops-metric-primary">
                          <Statistic title="Monthly visits" value={formatSummaryValue('visits', visits)} />
                          <div className="leadops-metric-signal">
                            {typeof visitsMomPct === 'number' && visitsMomPct !== 0 && (
                              <Tooltip
                                  title={
                                    typeof visitsPrevMonth === 'number'
                                      ? `Last month: ${formatCompact(visitsPrevMonth)} visits`
                                      : `${visitsMomPct > 0 ? '+' : ''}${visitsMomPct}% vs last month`
                                  }
                                  placement="right"
                                >
                                <Space size={4}>
                                  <span style={{ color: visitsMomPct > 0 ? '#52c41a' : '#ff4d4f' }}>
                                    {visitsMomPct > 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                                  </span>
                                  <Text
                                    type="secondary"
                                    style={{ fontSize: 12, color: visitsMomPct > 0 ? '#52c41a' : '#ff4d4f' }}
                                  >
                                    {visitsMomPct > 0
                                      ? (visitsMomPct >= 20 ? 'Traffic on fire' : visitsMomPct >= 5 ? 'Grew this month' : 'Slight uptick')
                                      : (visitsMomPct <= -20 ? 'Cooling off' : visitsMomPct <= -5 ? 'Down from last month' : 'Small dip')}
                                  </Text>
                                </Space>
                              </Tooltip>
                            )}
                          </div>
                        </div>
                        <div className="leadops-metric">
                          <Statistic title="Unique visitors" value={formatSummaryValue('unique_visitors', uniqueVisitors)} />
                        </div>
                        <div className="leadops-metric">
                          <Statistic title="Avg. visit duration" value={formatSummaryValue('avg_visit_duration', avgDuration)} />
                          <div className="leadops-metric-signal">
                            {durationColor !== 'default' && (
                              <Tag color={durationColor}>{durationColor === 'green' ? 'Strong duration' : 'Low duration'}</Tag>
                            )}
                          </div>
                        </div>
                        <div className="leadops-metric">
                          <Statistic title="Bounce rate" value={formatSummaryValue('bounce_rate', bounceRate)} />
                          <div className="leadops-metric-signal">
                            {bounceColor !== 'default' && (
                              <Tag color={bounceColor}>{bounceColor === 'green' ? 'Healthy' : 'High bounce'}</Tag>
                            )}
                          </div>
                        </div>
                        <div className="leadops-metric">
                          <Statistic title="Pages / visit" value={formatSummaryValue('pages_per_visit', pagesPerVisit)} />
                        </div>
                        <div className="leadops-metric">
                          <Statistic title="Desktop share" value={formatSummaryValue('desktop_share', desktopShare)} />
                        </div>
                        <div className="leadops-metric">
                          <Statistic title="Mobile share" value={formatSummaryValue('mobile_share', mobileShare)} />
                        </div>
                      </div>

                      <section className="leadops-geo-section">
                        <div className="leadops-section-heading">
                          <Text strong>Top GEO</Text>
                          {geoCountries.length > 0 && (
                            <Tooltip title="Copy Top GEO">
                              <Button
                                aria-label="Copy Top GEO"
                                className="leadops-section-copy"
                                type="text"
                                size="small"
                                icon={<CopyOutlined />}
                                onClick={handleCopyTopGeos}
                              >
                                <span className="desktop-label">Copy</span>
                              </Button>
                            </Tooltip>
                          )}
                        </div>
                        {topCountries.length === 0 && swWarnings.includes('geo_unavailable') && (
                          <Alert
                            type="warning"
                            showIcon
                            message="Similarweb GEO data unavailable for this API key."
                          />
                        )}
                        {topCountries.length === 0 && !swWarnings.includes('geo_unavailable') && (
                          <Text type="secondary">No data returned.</Text>
                        )}
                        {geoCountries.length > 0 && (
                          <div className="leadops-geo-list">
                            {geoCountries.map((entry: any, index: number) => {
                              const name = entry.country?.name || 'Unknown';
                              const code = entry.country?.alpha2 || '—';
                              const share = Math.min(100, Math.max(0, (entry.share || 0) * 100));
                              return (
                                <div className="leadops-geo-row" key={name || code}>
                                  <span className="leadops-geo-rank">{index + 1}</span>
                                  <span className="leadops-geo-country">
                                    <span className="leadops-geo-name">{name}</span>
                                    <span className="leadops-geo-code">{code}</span>
                                  </span>
                                  <span className="leadops-geo-bar" aria-hidden="true">
                                    <span style={{ width: `${share}%` }} />
                                  </span>
                                  <span className="leadops-geo-share">{formatPercent(share)}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </section>

                      <section className="leadops-deep-dive">
                        <div className="leadops-section-heading">
                          <Text strong>Deep-dive</Text>
                          <div className="leadops-deep-actions">
                          <Button
                            icon={<ThunderboltOutlined />}
                            onClick={handleTrafficSources}
                            disabled={!lastReadDomain}
                            loading={trafficSourcesMutation.isPending}
                          >
                            <span className="desktop-label">Traffic Sources</span><span className="mobile-label">Sources</span>
                          </Button>
                          <Button
                            icon={<TrophyOutlined />}
                            onClick={handleCompetitors}
                            disabled={!lastReadDomain}
                            loading={competitorsMutation.isPending}
                          >
                            Competitors
                          </Button>
                          <Tooltip title="Refresh Similarweb">
                            <Button
                              aria-label="Refresh Similarweb"
                              icon={<ReloadOutlined />}
                              onClick={() => lastReadDomain && handleCheckSw(lastReadDomain, 'full')}
                              disabled={!lastReadDomain || isAnyCheckPending}
                              loading={checkSwMutation.isPending}
                            >
                              <span className="desktop-label">Refresh</span>
                            </Button>
                          </Tooltip>
                          </div>
                        </div>

                      {(trafficSourcesMutation.data?.data || competitorsMutation.data?.data) && (
                        <div className="leadops-deep-results">
                          {trafficSourcesMutation.data?.data && (
                            <TrafficSourcesCard
                              trafficSources={trafficSources}
                              error={trafficSourcesMutation.data?.data?.error}
                            />
                          )}
                          {competitorsMutation.data?.data && (
                            <CompetitorsCard
                              competitors={competitors}
                              error={competitorsMutation.data?.data?.error}
                            />
                          )}
                        </div>
                      )}
                      </section>
                    </>
                  )}
                  </>
                ),
              },
              ].filter(Boolean) as { key: string; label: React.ReactNode; children: React.ReactNode }[];
              return panels;
            })()}
          />
        </>
      )}
        </>
      )}
      <InventoryPitchModal
        open={Boolean(pitchInventoryMatch)}
        match={pitchInventoryMatch}
        domain={pitchInventoryDomain}
        domains={pitchInventoryDomains}
        onDomainChange={handleInventoryPitchDomainChange}
        desktopShare={desktopShare}
        mobileShare={mobileShare}
        topCountries={topCountries}
        trafficSources={trafficSources}
        swLoading={checkSwMutation.isPending}
        trafficSourcesLoading={trafficSourcesMutation.isPending}
        trafficSourcesError={
          trafficSourcesMutation.data?.data?.error
          || (trafficSourcesMutation.isError ? 'unavailable' : undefined)
        }
        onClose={() => {
          setPitchInventoryMatch(null);
          setPitchInventoryDomain('');
          setPitchInventoryDomains([]);
        }}
      />
    </div>
  );
};

export default LeadOpsPage;
